import { useScriptTag } from '@vueuse/core'
import { onMounted, onUnmounted, type Ref, ref } from 'vue'
import { getWindow } from '@/helpers/globals.ts'
import type { ThreediumInitOptions } from '@/types/threedium.ts'
import { useSegment } from '@/third-party/segment'
import { logUnexpectedError } from '@/helpers'

/**
 * Parts are the geometric pieces that combined create the 3D thing we're visualising
 *
 * Materials are then applied on top of the parts to make them feel like the real material
 * and can have textures, colour, etc
 *
 * We can change the colour of materials on init via init.configuration.materials[name].color, etc - see example in docs
 *
 * Round shape and Oval shape are different parts
 * configuration.parts.show([round])
 * configuration.parts.hide([all, other, shapes])
 *
 * For Metal we do as above
 *
 * Transitions are meant for moving things around and doing basic things to only one thing
 * any transition will have time as a parameter
 *
 * If you want to change gold to silver you're still using the same material
 * But you are changing colour or some other parameter
 *
 * Modifiers change the material entity completely instantly, but if the modifier
 * has a configured transition, it can also launch the transition as well
 * Modifiers have more options
 * Transitions only change colour or some other property of a material and/or move
 * the camera to have a nice overview
 * Modifiers can activate transitions BUT also do hide/show part and also change
 * the material to a completely different material so you apply a part to a material
 * You can create modifier groups that combine multiple modifiers
 */
export interface ThreediumControls {
    loading: Ref<boolean>
    isPrepared: Ref<boolean>

    metal: Ref<ThreediumMetal | undefined>
    shape: Ref<ThreediumShape | undefined>
    band: Ref<ThreediumBandType | undefined>
    caratSize: Ref<ThreediumCaratSize | undefined>
    centreStone: Ref<ThreediumCentreStone | undefined>
    sideStone: Ref<ThreediumSideStone | undefined>
    signatureStone: Ref<ThreediumSignatureStone | undefined>
    engraving: Ref<string>
    seeOnHand: Ref<boolean>

    selectMetal(P: ThreediumMetal, animate: boolean): Promise<void>
    selectShape(P: ThreediumShape, animate: boolean): Promise<void>
    selectBand(P: ThreediumBandType, animate: boolean): Promise<void>
    selectCentreStone(P: ThreediumCentreStone, animate: boolean): Promise<void>
    selectSideStone(P: ThreediumSideStone, animate: boolean): Promise<void>
    selectSignatureStone(
        P: ThreediumSignatureStone,
        animate: boolean
    ): Promise<void>
    selectCaratSize(P: ThreediumCaratSize, animate: boolean): Promise<void>
    updateEngraving(text: string): void
    toggleSeeOnHand(): Promise<void>

    onEngravingFocus(): Promise<void>
    onEngravingBlur(): Promise<void>

    onLoad(callback: () => void): void

    trackThreediumFilters: TrackThreediumFilters
}

export type ThreediumInitParameters = {
    threediumMetal: ThreediumMetal | undefined
    centreStoneShape: ThreediumShape | undefined
    sideStone: ThreediumSideStone | undefined
    band: ThreediumBandType | undefined
}

function getUnlimited3D(): Window['Unlimited3D'] {
    const window = getWindow()

    if (!window) {
        return {
            init() {},
            // SLOW - multiple things
            activateModifier() {},
            // QUICK - one thing
            activateTransition() {},
            setPartTransformationProperties() {},
            updateOverlay() {},
            dispose() {},
            activateCameraControl() {},
        }
    }

    return window.Unlimited3D
}

export function useThreedium(): ThreediumControls {
    const { caratSizes } = useThreediumValues()

    const { load: loadThreedium } = useScriptTag(
        () =>
            'https://distcdn.unlimited3d.com/pres/v/3.0.18/unlimited3d.min.js',
        undefined,
        {
            manual: true,
        }
    )

    const loading = ref(true)
    const isPrepared = ref(false)

    const options: ThreediumInitOptions = {
        distID: 'latest',
        solution3DName: 'thia-ring-master-v2',
        projectName: 'taylor-hart-mvp',
        solution3DID: '93174',
        containerID: 'threedium',
        onLoadingChanged(e) {
            loading.value = Math.round(e.progress) !== 100
        },
    }

    let Unlimited3D = getUnlimited3D()

    let initStarted = false

    let onLoadCallback: null | (() => void) = null

    const initThreedium = () => {
        if (initStarted) {
            return
        }

        Unlimited3D = getUnlimited3D()

        if (!Unlimited3D) {
            return
        }

        initStarted = true

        Unlimited3D.init(options, {}, function (error, status) {
            if (error || !status) {
                return
            }

            if (onLoadCallback) {
                onLoadCallback()
            }

            loading.value = false
        })
    }

    let isMounted = false

    onMounted(() => {
        isMounted = true

        initThreedium()
    })

    loadThreedium(true).then(() => {
        if (isMounted) {
            initThreedium()
        }
    })

    onUnmounted(() => {
        Unlimited3D?.dispose()
    })

    function onLoad(callback: () => void) {
        if (!loading.value) {
            callback()
        } else {
            onLoadCallback = callback
        }
    }

    async function activateModifier(modifier: string): Promise<void> {
        return new Promise((resolve) => {
            Unlimited3D?.activateModifier({ modifier }, () => {
                resolve()
            })
        })
    }

    const currentCamera = ref('Camera Central Stone')

    async function activateCamera(camera: string): Promise<void> {
        if (currentCamera.value !== camera) {
            currentCamera.value = camera

            await activateModifier(camera)
        }
    }

    async function activateTransition(
        transition: string,
        duration: number | undefined = undefined
    ): Promise<void> {
        return new Promise((resolve) => {
            Unlimited3D?.activateTransition(
                {
                    transition,
                    duration,
                },
                resolve
            )
        })
    }

    function updateOverlay(
        text: string,
        overlay: string,
        overlayEntry: string | null = null
    ) {
        if (!overlayEntry) {
            overlayEntry = overlay
        }

        Unlimited3D?.updateOverlay({
            overlay,
            overlayEntry,
            options: { text },
        })
    }

    function setPartTransformationProperties(
        name: string,
        scale: [number, number, number]
    ) {
        Unlimited3D?.setPartTransformationProperties({
            name,
            scale,
        })
    }

    const metal: Ref<ThreediumMetal | undefined> = ref()
    const shape: Ref<ThreediumShape | undefined> = ref()
    const caratSize: Ref<ThreediumCaratSize> = ref(caratSizes[0]!)
    const band: Ref<ThreediumBandType | undefined> = ref()
    const centreStone: Ref<ThreediumCentreStone | undefined> = ref()
    const sideStone: Ref<ThreediumSideStone | undefined> = ref()
    const signatureStone: Ref<ThreediumSignatureStone | undefined> = ref()
    const engraving: Ref<string> = ref('')

    async function selectMetal(P: ThreediumMetal, animate: boolean) {
        if (animate) {
            activateCamera('Camera Metal')
        }

        if (metal.value?.name !== P.name) {
            metal.value = P
            await activateTransition(P.mod)
        }
    }

    async function selectShape(P: ThreediumShape, animate: boolean) {
        if (animate) {
            await activateCamera('Camera Central Stone')
        }

        if (shape.value?.name !== P.name) {
            shape.value = P
            const y = caratSize.value?.val + '_' + P.name.toLowerCase()
            await activateModifier(y)
            scale()
        }
    }

    async function selectBand(P: ThreediumBandType, animate: boolean) {
        if (animate) {
            activateCamera('Camera Metal')
        }

        if (band.value?.name !== P.name) {
            band.value = P

            if (P.name === 'Diamond band') {
                await activateModifier('Diamond Band')
            } else {
                await activateModifier('Plain Band')
            }
        }
    }

    async function selectCentreStone(
        P: ThreediumCentreStone,
        animate: boolean
    ) {
        if (animate) {
            activateCamera('Camera Central Stone')
        }

        if (centreStone.value?.name !== P.name) {
            centreStone.value = P
            await activateModifier(P.mod)
        }
    }

    async function selectSideStone(P: ThreediumSideStone, animate: boolean) {
        if (animate) {
            activateCamera('Camera Side Stones')
        }

        if (sideStone.value?.name !== P.name) {
            sideStone.value = P

            await activateTransition(P.mod)
        }
    }

    async function selectSignatureStone(
        P: ThreediumSignatureStone,
        animate: boolean
    ) {
        if (animate) {
            activateCamera('Engr Camera')
        }

        if (signatureStone.value?.name !== P.name) {
            signatureStone.value = P
            await activateTransition(P.mod)
        }
    }

    async function selectCaratSize(P: ThreediumCaratSize, animate: boolean) {
        if (animate) {
            activateCamera('Camera Central Stone')
        }

        // todo this needs improvement
        if (shape.value) {
            caratSize.value = P
            const y = caratSize.value.val + '_' + shape.value.name.toLowerCase()
            await activateModifier(y)
            scale()
        }
    }

    function updateEngraving(text: string): void {
        engraving.value = text
        updateOverlay(text, 'Engraving-1')
    }

    function scale(): void {
        if (!shape.value) {
            // todo this needs improvement
            return
        }

        if (!caratSize.value) {
            // todo this needs improvement
            return
        }

        const P = shape.value.part
        const y = shape.value.sizes[caratSize.value.size] as number

        setPartTransformationProperties(P, [y, y, y])
    }

    const seeOnHand = ref(false)

    const { trackThreediumFilters, trackThreediumViewer } =
        useThreediumTracking()

    async function toggleSeeOnHand() {
        seeOnHand.value = !seeOnHand.value
        await activateModifier(seeOnHand.value ? 'with hand' : 'just ring')
        await Unlimited3D?.activateCameraControl({
            name: seeOnHand.value ? 'CC Hand' : 'CC Publish',
        })

        trackThreediumViewer(seeOnHand.value ? 'on_hand' : 'ring_only')
    }

    async function onEngravingFocus(): Promise<void> {
        await activateCamera('Engr Camera')
    }

    async function onEngravingBlur(): Promise<void> {
        await activateCamera('Camera Init')
    }

    return {
        loading,
        isPrepared,

        metal,
        shape,
        band,
        caratSize,
        centreStone,
        sideStone,
        signatureStone,
        engraving,
        seeOnHand,

        selectMetal,
        selectShape,
        selectBand,
        selectCentreStone,
        selectSideStone,
        selectSignatureStone,
        selectCaratSize,
        updateEngraving,
        toggleSeeOnHand,
        onEngravingFocus,
        onEngravingBlur,

        onLoad,

        trackThreediumFilters,
    }
}

export type ThreediumValues = {
    metals: ThreediumMetal[]
    shapes: ThreediumShape[]
    bands: ThreediumBandType[]
    caratSizes: ThreediumCaratSize[]
    centreStones: ThreediumCentreStone[]
    sideStones: ThreediumSideStone[]
    signatureStones: ThreediumSignatureStone[]
}

export function useThreediumValues(): ThreediumValues {
    const metals = getThreediumMetals()
    const shapes = getThreediumShapes()
    const bands: ThreediumBandType[] = getThreediumBandTypes()
    const caratSizes = getThreediumCaratSizes()
    const centreStones = getThreediumCentreStones()
    const sideStones = getThreediumSideStones()
    const signatureStones = getThreediumSignatureStones()

    return {
        metals,
        shapes,
        bands,
        caratSizes,
        centreStones,
        sideStones,
        signatureStones,
    }
}

export type ThreediumMetal = {
    name: string
    label: string
    color: string
    price: number
    mod: string
}

function getThreediumMetals(): ThreediumMetal[] {
    return [
        {
            name: 'Platinum',
            label: 'PT',
            color: '#efefef',
            price: 2e3,
            mod: 'Platinum',
        },
        {
            name: '18ct Rose Gold',
            label: '18',
            color: '#efcaad',
            price: 1650,
            mod: 'Rose Gold',
        },
        {
            name: '18ct Yellow Gold',
            label: '18',
            color: '#e7d698',
            price: 1650,
            mod: 'Yellow Gold',
        },
        {
            name: '18ct White Gold',
            label: '18',
            color: '#c8c8c8',
            price: 1650,
            mod: 'White Gold',
        },
    ]
}

export type ThreediumShape = {
    name: string
    sizes: number[]
    mod: string
    part: string
    icon: string
}
function getThreediumShapes(): ThreediumShape[] {
    return [
        {
            name: 'Emerald',
            sizes: [0, 1, 1.15, 1.275],
            mod: 'Main_Emerald',
            part: 'thia_ring_test2_Skey:Diamond_main_emerald',
            icon: 'https://taylorandhart.com/media/assets/6e52519d390308abe5a3b0a4cb4774bb.svg',
        },
        {
            name: 'Oval',
            sizes: [0, 1, 1.2, 1.348],
            mod: 'Main_Oval',
            part: 'thia_ring_test2_Skey:Diamond_main_oval',
            icon: 'https://taylorandhart.com/media/assets/91add4aaba34adf60e5a8e7256125c15.svg',
        },
        {
            name: 'Radiant',
            sizes: [0, 0.97, 1.13, 1.22],
            mod: 'Main_Radiant',
            part: 'thia_ring_test2_Skey:Diamond_main_radiant',
            icon: 'https://taylorandhart.com/media/assets/45833141a7cf43a9b4004a4a4432f349.svg',
        },
        {
            name: 'Round',
            sizes: [0, 0.97, 1.15, 1.26],
            mod: 'Main_Round',
            part: 'thia_ring_test2_Skey:Diamond_main_round',
            icon: 'https://taylorandhart.com/media/assets/d84933b2aba4cf59a641039c2efa34c7.svg',
        },
    ]
}

export type ThreediumBandType = {
    name: string
}
function getThreediumBandTypes(): ThreediumBandType[] {
    return [{ name: 'Plain band' }, { name: 'Diamond band' }]
}

export type ThreediumCaratSize = {
    name: string
    size: number
    shape: number
    val: 'small' | 'medium' | 'large'
}

function getThreediumCaratSizes(): ThreediumCaratSize[] {
    return [
        { name: '1ct', size: 1, shape: 0, val: 'small' },
        { name: '2ct', size: 2, shape: 0.5, val: 'medium' },
        { name: '3ct', size: 3, shape: 1, val: 'large' },
    ]
}

export type ThreediumCentreStone = {
    name: string
    mod: string
    icon: string
}

function getThreediumCentreStones(): ThreediumCentreStone[] {
    return [
        {
            name: 'Natural Diamond',
            mod: 'CenterColor1',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103713/white-diamond.png',
        },
        {
            name: 'Ruby',
            mod: 'CenterColor2',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103631/Ruby-1.png',
        },
        {
            name: 'Yellow Sapphire',
            mod: 'CenterColor3',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103650/Yellow-sapphire.png',
        },
        {
            name: 'Emerald',
            mod: 'CenterColor4',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103705/Emerald.png',
        },
        {
            name: 'Orange Sapphire',
            mod: 'CenterColor5',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103921/orange-sapphire-1.png',
        },
        {
            name: 'Spinel',
            mod: 'CenterColor6',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103727/black-spinel-1.png',
        },
        {
            name: 'Zircon',
            mod: 'CenterColor7',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103735/blue-zircon-sig-stone-customiser.png',
        },
        {
            name: 'Tanzanite',
            mod: 'CenterColor8',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103751/tanzanite-1.png',
        },
        {
            name: 'Tsavorite',
            mod: 'CenterColor9',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103743/tsavorite-1.png',
        },
    ]
}

export type ThreediumSideStone = {
    name: string
    mod: string
    icon: string
}

function getThreediumSideStones(): ThreediumSideStone[] {
    return [
        {
            name: 'Diamond',
            mod: 'Side Diamond',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103713/white-diamond.png',
        },
        {
            name: 'Green Sapphire',
            mod: 'Side Green Sapphire',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103915/green-sapphier.png',
        },
        {
            name: 'Purple Sapphire',
            mod: 'Side Purple Sapphire',
            icon: '/assets/images/purple_sapphire.png',
        },
        {
            name: 'Pink Sapphire',
            mod: 'Side Pink Sapphire',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103658/Pink-sapphire.png',
        },
        {
            name: 'Ruby',
            mod: 'Side Ruby',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103631/Ruby-1.png',
        },
    ]
}

export type ThreediumSignatureStone = {
    name: string
    mod: string
    icon: string
    colourSystemCode: string
    crystalSystemCode: string
}

function getThreediumSignatureStones(): ThreediumSignatureStone[] {
    return [
        {
            name: 'Ruby',
            mod: 'Signature Ruby',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103631/Ruby-1.png',
            colourSystemCode: 'GEMSTONE_RED',
            crystalSystemCode: 'RUBY',
        },
        {
            name: 'Blue Sapphire',
            mod: 'Signature Blue Sapphire',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103642/Blue-sapphire.png',
            colourSystemCode: 'GEMSTONE_BLUE',
            crystalSystemCode: 'SAPPHIRE',
        },
        {
            name: 'Yellow Sapphire',
            mod: 'Signature Yellow Sapphire',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103650/Yellow-sapphire.png',
            colourSystemCode: 'GEMSTONE_YELLOW',
            crystalSystemCode: 'SAPPHIRE',
        },
        {
            name: 'Pink Sapphire',
            mod: 'Signature Pink Sapphire',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103658/Pink-sapphire.png',
            colourSystemCode: 'GEMSTONE_PINK',
            crystalSystemCode: 'SAPPHIRE',
        },
        {
            name: 'Emerald',
            mod: 'Signature Emerald',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103705/Emerald.png',
            colourSystemCode: 'GEMSTONE_GREEN',
            crystalSystemCode: 'EMERALD',
        },
        {
            name: 'Diamond',
            mod: 'Signature Diamond',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103713/white-diamond.png',
            colourSystemCode: 'DIAMOND_G',
            crystalSystemCode: 'DIAMOND',
        },
        {
            name: 'Black Spinel',
            mod: 'Signature Black Spinel',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103727/black-spinel-1.png',
            colourSystemCode: 'GEMSTONE_BLACK',
            crystalSystemCode: 'SPINEL',
        },
        {
            name: 'Tsavorite',
            mod: 'Signature Tsavorite',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103743/tsavorite-1.png',
            colourSystemCode: 'GEMSTONE_GREEN',
            crystalSystemCode: 'TSAVORITE',
        },
        {
            name: 'Tanzanite',
            mod: 'Signature Tanzanite',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103751/tanzanite-1.png',
            colourSystemCode: 'GEMSTONE_BLUISH_VIOLET',
            crystalSystemCode: 'TANZANITE',
        },
        {
            name: 'Citrine',
            mod: 'Signature Citrine',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103803/citrine-1.png',
            colourSystemCode: 'GEMSTONE_ORANGE',
            crystalSystemCode: 'CITRINE',
        },
        {
            name: 'Blue Tourmaline',
            mod: 'Signature Blue Tourmaline',
            icon: 'https://cms-media.taylorandhart.com/2023/06/30165850/tourmaline-lighter2.png',
            colourSystemCode: 'GEMSTONE_BLUE',
            crystalSystemCode: 'TOURMALINE',
        },
        {
            name: 'Green Tourmaline',
            mod: 'Signature Green Tourmaline',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103817/green-tourmaline-1.png',
            colourSystemCode: 'GEMSTONE_GREEN',
            crystalSystemCode: 'TOURMALINE',
        },
        {
            name: 'Pink Tourmaline',
            mod: 'Signature Pink Tourmaline',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103824/pink-tourmaline-1.png',
            colourSystemCode: 'GEMSTONE_PINK',
            crystalSystemCode: 'TOURMALINE',
        },
        {
            name: 'Peridot',
            mod: 'Signature Peridot',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103833/peridot-1.png',
            colourSystemCode: 'GEMSTONE_GREEN',
            crystalSystemCode: 'PERIDOT',
        },
        {
            name: 'Alexandrite',
            mod: 'Signature Alexandrite',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103840/alexandrite-1.png',
            colourSystemCode: 'GEMSTONE_GREENISH_GRAY',
            crystalSystemCode: 'ALEXANDRITE',
        },
        {
            name: 'Aquamarine',
            mod: 'Signature Aquamarine',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103907/aquamarine-1.png',
            colourSystemCode: 'GEMSTONE_BLUE',
            crystalSystemCode: 'AQUAMARINE',
        },
        {
            name: 'Green Sapphire',
            mod: 'Signature Green Sapphire',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103915/green-sapphier.png',
            colourSystemCode: 'GEMSTONE_GREEN',
            crystalSystemCode: 'SAPPHIRE',
        },
        {
            name: 'Orange Sapphire',
            mod: 'Signature Orange Sapphire',
            icon: 'https://cms-media.taylorandhart.com/2023/06/06103921/orange-sapphire-1.png',
            colourSystemCode: 'GEMSTONE_ORANGE',
            crystalSystemCode: 'SAPPHIRE',
        },
        {
            name: 'Amethyst',
            mod: 'Signature Amethyst',
            icon: 'https://cms-media.taylorandhart.com/2023/06/17091810/amethyst1.png',
            colourSystemCode: 'GEMSTONE_PURPLE',
            crystalSystemCode: 'AMETHYST',
        },
    ]
}

type ThreediumTrackingFilterName =
    | 'metal'
    | 'centre_shape'
    | 'carat_size'
    | 'side_stones'
    | 'band_type'
    | 'signature_stone'

type ThreediumTrackingViewerViews = 'ring_only' | 'on_hand'
type TrackThreediumViewer = (value: ThreediumTrackingViewerViews) => void
type TrackThreediumFilters = (
    filterName: ThreediumTrackingFilterName,
    value: string
) => void

const useThreediumTracking = (): {
    trackThreediumFilters: TrackThreediumFilters
    trackThreediumViewer: TrackThreediumViewer
} => {
    const { track } = useSegment()
    const window = getWindow()

    const trackThreediumFilters = (
        filterName: ThreediumTrackingFilterName,
        value: string
    ) => {
        if (!window) {
            logUnexpectedError('Auth tracking: Unable to find window', {})

            return
        }

        track('custom_threedium_filter_select', {
            param1: 'threedium_filter',
            param2: 'select',
            param3: filterName.replaceAll(' ', '_').toLocaleLowerCase(),
            param4: value.replaceAll(' ', '_').toLocaleLowerCase(),
            url: window.location.href,
            event_name: 'threedium_filter_select',
        })
    }

    const trackThreediumViewer = (value: ThreediumTrackingViewerViews) => {
        if (!window) {
            logUnexpectedError('Auth tracking: Unable to find window', {})

            return
        }

        track('custom_threedium_view_select', {
            param1: 'threedium_view',
            param2: 'select',
            param3: 'view_mode',
            param4: value,
            url: window.location.href,
            event_name: 'threedium_view_select',
        })
    }

    return {
        trackThreediumFilters,
        trackThreediumViewer,
    }
}

// TODO Get all options via Unlimited3D.getAvailableTransitions(console.log)
// [
//     {
//         "type": "transition",
//         "shortName": "Yellow Gold",
//         "name": "Yellow Gold",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Rose Gold",
//         "name": "Rose Gold",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "White Gold",
//         "name": "White Gold",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Platinum",
//         "name": "Platinum",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "CenterColor1",
//         "name": "CenterColor1",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "CenterColor2",
//         "name": "CenterColor2",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "CenterColor3",
//         "name": "CenterColor3",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "CenterColor4",
//         "name": "CenterColor4",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "CenterColor5",
//         "name": "CenterColor5",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "CenterColor6",
//         "name": "CenterColor6",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "CenterColor7",
//         "name": "CenterColor7",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "CenterColor8",
//         "name": "CenterColor8",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "CenterColor9",
//         "name": "CenterColor9",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Side Diamond",
//         "name": "Side Diamond",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Side Ruby",
//         "name": "Side Ruby",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Side Green Sapphire",
//         "name": "Side Green Sapphire",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Side Pink Sapphire",
//         "name": "Side Pink Sapphire",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Side Purple Sapphire",
//         "name": "Side Purple Sapphire",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "SideColor6",
//         "name": "SideColor6",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "SideColor7",
//         "name": "SideColor7",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "SideColor8",
//         "name": "SideColor8",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "SideColor9",
//         "name": "SideColor9",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Ruby",
//         "name": "Signature Ruby",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Diamond",
//         "name": "Signature Diamond",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Yellow Sapphire",
//         "name": "Signature Yellow Sapphire",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Pink Sapphire",
//         "name": "Signature Pink Sapphire",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Emerald",
//         "name": "Signature Emerald",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Black Spinel",
//         "name": "Signature Black Spinel",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Tsavorite",
//         "name": "Signature Tsavorite",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Tanzanite",
//         "name": "Signature Tanzanite",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Citrine",
//         "name": "Signature Citrine",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Camera Init",
//         "name": "Camera Init",
//         "targetType": "camera"
//     },
//     {
//         "type": "transition",
//         "shortName": "Camera hand",
//         "name": "Camera hand",
//         "targetType": "camera"
//     },
//     {
//         "type": "transition",
//         "shortName": "hand on",
//         "name": "hand on",
//         "targetType": "part"
//     },
//     {
//         "type": "transition",
//         "shortName": "hand off",
//         "name": "hand off",
//         "targetType": "part"
//     },
//     {
//         "type": "transition",
//         "shortName": "Camera Engraving",
//         "name": "Camera Engraving",
//         "targetType": "camera"
//     },
//     {
//         "type": "transition",
//         "shortName": "Camera Metal",
//         "name": "Camera Metal",
//         "targetType": "camera"
//     },
//     {
//         "type": "transition",
//         "shortName": "Camera Central Stone",
//         "name": "Camera Central Stone",
//         "targetType": "camera"
//     },
//     {
//         "type": "transition",
//         "shortName": "Camera Side Stones",
//         "name": "Camera Side Stones",
//         "targetType": "camera"
//     },
//     {
//         "type": "transition",
//         "shortName": "shadow_on",
//         "name": "shadow_on",
//         "targetType": "part"
//     },
//     {
//         "type": "transition",
//         "shortName": "shadow_off",
//         "name": "shadow_off",
//         "targetType": "part"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Orange Sapphire",
//         "name": "Signature Orange Sapphire",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Green Sapphire",
//         "name": "Signature Green Sapphire",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Aquamarine",
//         "name": "Signature Aquamarine",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Alexandrite",
//         "name": "Signature Alexandrite",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Peridot",
//         "name": "Signature Peridot",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Pink Tourmaline",
//         "name": "Signature Pink Tourmaline",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Green Tourmaline",
//         "name": "Signature Green Tourmaline",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Blue Tourmaline",
//         "name": "Signature Blue Tourmaline",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Amethyst",
//         "name": "Signature Amethyst",
//         "targetType": "material"
//     },
//     {
//         "type": "transition",
//         "shortName": "Signature Blue Sapphire",
//         "name": "Signature Blue Sapphire",
//         "targetType": "material"
//     }
// ]

// TODO Get all options via Unlimited3D.getAvailableModifiers(console.log)
// [
//     {
//         "type": "modifier",
//         "shortName": "HIDE_EMERALD",
//         "name": "HIDE_EMERALD"
//     },
//     {
//         "type": "modifier",
//         "shortName": "small_emerald",
//         "name": "small_emerald"
//     },
//     {
//         "type": "modifier",
//         "shortName": "small_oval",
//         "name": "small_oval"
//     },
//     {
//         "type": "modifier",
//         "shortName": "small_radiant",
//         "name": "small_radiant"
//     },
//     {
//         "type": "modifier",
//         "shortName": "small_round",
//         "name": "small_round"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Yellow Gold",
//         "name": "Yellow Gold"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Rose Gold",
//         "name": "Rose Gold"
//     },
//     {
//         "type": "modifier",
//         "shortName": "White Gold",
//         "name": "White Gold"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Platinum",
//         "name": "Platinum"
//     },
//     {
//         "type": "modifier",
//         "shortName": "CenterColor1",
//         "name": "CenterColor1"
//     },
//     {
//         "type": "modifier",
//         "shortName": "CenterColor2",
//         "name": "CenterColor2"
//     },
//     {
//         "type": "modifier",
//         "shortName": "CenterColor3",
//         "name": "CenterColor3"
//     },
//     {
//         "type": "modifier",
//         "shortName": "CenterColor4",
//         "name": "CenterColor4"
//     },
//     {
//         "type": "modifier",
//         "shortName": "CenterColor5",
//         "name": "CenterColor5"
//     },
//     {
//         "type": "modifier",
//         "shortName": "CenterColor6",
//         "name": "CenterColor6"
//     },
//     {
//         "type": "modifier",
//         "shortName": "CenterColor7",
//         "name": "CenterColor7"
//     },
//     {
//         "type": "modifier",
//         "shortName": "CenterColor9",
//         "name": "CenterColor9"
//     },
//     {
//         "type": "modifier",
//         "shortName": "CenterColor8",
//         "name": "CenterColor8"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SideColor1",
//         "name": "SideColor1"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SideColor2",
//         "name": "SideColor2"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SideColor3",
//         "name": "SideColor3"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SideColor4",
//         "name": "SideColor4"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SideColor5",
//         "name": "SideColor5"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SideColor6",
//         "name": "SideColor6"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SideColor7",
//         "name": "SideColor7"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SideColor8",
//         "name": "SideColor8"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SideColor9",
//         "name": "SideColor9"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SignatureColor1",
//         "name": "SignatureColor1"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SignatureColor2",
//         "name": "SignatureColor2"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SignatureColor3",
//         "name": "SignatureColor3"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SignatureColor4",
//         "name": "SignatureColor4"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SignatureColor5",
//         "name": "SignatureColor5"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SignatureColor6",
//         "name": "SignatureColor6"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SignatureColor7",
//         "name": "SignatureColor7"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SignatureColor8",
//         "name": "SignatureColor8"
//     },
//     {
//         "type": "modifier",
//         "shortName": "SignatureColor9",
//         "name": "SignatureColor9"
//     },
//     {
//         "type": "modifier",
//         "shortName": "just ring",
//         "name": "just ring"
//     },
//     {
//         "type": "modifier",
//         "shortName": "with hand",
//         "name": "with hand"
//     },
//     {
//         "type": "modifier",
//         "shortName": "hand on",
//         "name": "hand on"
//     },
//     {
//         "type": "modifier",
//         "shortName": "hand off",
//         "name": "hand off"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Camera Init",
//         "name": "Camera Init"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Camera hand",
//         "name": "Camera hand"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Camera Engraving",
//         "name": "Camera Engraving"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Engr Camera",
//         "name": "Engr Camera"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Camera Metal",
//         "name": "Camera Metal"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Camera Central Stone",
//         "name": "Camera Central Stone"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Camera Side Stones",
//         "name": "Camera Side Stones"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Plain Band",
//         "name": "Plain Band"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Diamond Band",
//         "name": "Diamond Band"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Emerald_top_hide",
//         "name": "Emerald_top_hide"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Oval_top_hide",
//         "name": "Oval_top_hide"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Emerald_main_hide",
//         "name": "Emerald_main_hide"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Oval_main_hide",
//         "name": "Oval_main_hide"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Round_main_hide",
//         "name": "Round_main_hide"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Radiant_main_hide",
//         "name": "Radiant_main_hide"
//     },
//     {
//         "type": "modifier",
//         "shortName": "DIAMOND_main_emerald",
//         "name": "DIAMOND_main_emerald"
//     },
//     {
//         "type": "modifier",
//         "shortName": "DIAMOND_main_round",
//         "name": "DIAMOND_main_round"
//     },
//     {
//         "type": "modifier",
//         "shortName": "DIAMOND_main_radiant",
//         "name": "DIAMOND_main_radiant"
//     },
//     {
//         "type": "modifier",
//         "shortName": "TOP_emerald",
//         "name": "TOP_emerald"
//     },
//     {
//         "type": "modifier",
//         "shortName": "TOP_oval",
//         "name": "TOP_oval"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Radiant_top_hide",
//         "name": "Radiant_top_hide"
//     },
//     {
//         "type": "modifier",
//         "shortName": "Round_top_hide",
//         "name": "Round_top_hide"
//     },
//     {
//         "type": "modifier",
//         "shortName": "DIAMOND_main_oval",
//         "name": "DIAMOND_main_oval"
//     },
//     {
//         "type": "modifier",
//         "shortName": "TOP_radiant",
//         "name": "TOP_radiant"
//     },
//     {
//         "type": "modifier",
//         "shortName": "TOP_round",
//         "name": "TOP_round"
//     },
//     {
//         "type": "modifier",
//         "shortName": "band_plain",
//         "name": "band_plain"
//     },
//     {
//         "type": "modifier",
//         "shortName": "band_diamond",
//         "name": "band_diamond"
//     },
//     {
//         "type": "modifier",
//         "shortName": "HIDE_band_plain",
//         "name": "HIDE_band_plain"
//     },
//     {
//         "type": "modifier",
//         "shortName": "HIDE_band_diamond",
//         "name": "HIDE_band_diamond"
//     },
//     {
//         "type": "modifier",
//         "shortName": "1_hidebanddiamonds",
//         "name": "1_hidebanddiamonds"
//     },
//     {
//         "type": "modifier",
//         "shortName": "1_banddiamonds",
//         "name": "1_banddiamonds"
//     },
//     {
//         "type": "modifier",
//         "shortName": "2_hidebanddiamonds",
//         "name": "2_hidebanddiamonds"
//     },
//     {
//         "type": "modifier",
//         "shortName": "2_banddiamonds",
//         "name": "2_banddiamonds"
//     },
//     {
//         "type": "modifier",
//         "shortName": "3_hidebanddiamonds",
//         "name": "3_hidebanddiamonds"
//     },
//     {
//         "type": "modifier",
//         "shortName": "4_hidebanddiamonds",
//         "name": "4_hidebanddiamonds"
//     },
//     {
//         "type": "modifier",
//         "shortName": "5_hidebanddiamonds",
//         "name": "5_hidebanddiamonds"
//     },
//     {
//         "type": "modifier",
//         "shortName": "6_hidebanddiamonds",
//         "name": "6_hidebanddiamonds"
//     },
//     {
//         "type": "modifier",
//         "shortName": "3_banddiamonds",
//         "name": "3_banddiamonds"
//     },
//     {
//         "type": "modifier",
//         "shortName": "4_banddiamonds",
//         "name": "4_banddiamonds"
//     },
//     {
//         "type": "modifier",
//         "shortName": "5_banddiamonds",
//         "name": "5_banddiamonds"
//     },
//     {
//         "type": "modifier",
//         "shortName": "6_banddiamonds",
//         "name": "6_banddiamonds"
//     },
//     {
//         "type": "modifier",
//         "shortName": "shadow_on",
//         "name": "shadow_on"
//     },
//     {
//         "type": "modifier",
//         "shortName": "shadow_off",
//         "name": "shadow_off"
//     },
//     {
//         "type": "modifier",
//         "shortName": "s_emerald",
//         "name": "s_emerald"
//     },
//     {
//         "type": "modifier",
//         "shortName": "s_oval",
//         "name": "s_oval"
//     },
//     {
//         "type": "modifier",
//         "shortName": "s_radiant",
//         "name": "s_radiant"
//     },
//     {
//         "type": "modifier",
//         "shortName": "s_round",
//         "name": "s_round"
//     },
//     {
//         "type": "modifier",
//         "shortName": "medium_emerald",
//         "name": "medium_emerald"
//     },
//     {
//         "type": "modifier",
//         "shortName": "m_emerald",
//         "name": "m_emerald"
//     },
//     {
//         "type": "modifier",
//         "shortName": "large_emerald",
//         "name": "large_emerald"
//     },
//     {
//         "type": "modifier",
//         "shortName": "l_emerald",
//         "name": "l_emerald"
//     },
//     {
//         "type": "modifier",
//         "shortName": "medium_oval",
//         "name": "medium_oval"
//     },
//     {
//         "type": "modifier",
//         "shortName": "m_oval",
//         "name": "m_oval"
//     },
//     {
//         "type": "modifier",
//         "shortName": "m_radiant",
//         "name": "m_radiant"
//     },
//     {
//         "type": "modifier",
//         "shortName": "m_round",
//         "name": "m_round"
//     },
//     {
//         "type": "modifier",
//         "shortName": "l_oval",
//         "name": "l_oval"
//     },
//     {
//         "type": "modifier",
//         "shortName": "l_radiant",
//         "name": "l_radiant"
//     },
//     {
//         "type": "modifier",
//         "shortName": "l_round",
//         "name": "l_round"
//     },
//     {
//         "type": "modifier",
//         "shortName": "large_oval",
//         "name": "large_oval"
//     },
//     {
//         "type": "modifier",
//         "shortName": "medium_radiant",
//         "name": "medium_radiant"
//     },
//     {
//         "type": "modifier",
//         "shortName": "large_radiant",
//         "name": "large_radiant"
//     },
//     {
//         "type": "modifier",
//         "shortName": "medium_round",
//         "name": "medium_round"
//     },
//     {
//         "type": "modifier",
//         "shortName": "large_round",
//         "name": "large_round"
//     },
//     {
//         "type": "modifier",
//         "shortName": "HIDE_OVAL",
//         "name": "HIDE_OVAL"
//     },
//     {
//         "type": "modifier",
//         "shortName": "HIDE_RADIANT",
//         "name": "HIDE_RADIANT"
//     },
//     {
//         "type": "modifier",
//         "shortName": "HIDE_ROUND",
//         "name": "HIDE_ROUND"
//     },
//     {
//         "type": "modifier",
//         "shortName": "side_diamonds_h",
//         "name": "side_diamonds_h"
//     }
// ]
